import * as React from 'react';

import { Slider } from '@/hen-ui/composites/ImageGrid/variants/Slider';
import { cn } from '@/hen-ui/utils';
import { GridImage } from '@/hen-ui/composites/GridImage';

interface GridProps {
  json?: {
    url: string;
    alt?: string;
  }[];
  width?: number;
  height?: number;
  children?: React.ReactNode;
}

type GridContentProps = React.PropsWithChildren<{
  json?: {
    url: string;
    alt?: string;
  }[];
  gridClass: string;
  width?: number;
  height?: number;
}>;

function GridContent({ children, json, gridClass, width }: GridContentProps) {
  // If children are provided, render them directly
  // Following React's guidance: avoid using Children utilities
  // Consumers should pass properly structured children (array of elements)

  if (json && json.length > 0) {
    return (
      <div role='list' className={cn(gridClass)} aria-label='Image grid'>
        {json.map((image, index) => (
          <GridImage
            key={index}
            src={image.url}
            alt={image.alt || `Grid Image ${index + 1}`}
            objectFit='contain'
            width={width}
          />
        ))}
      </div>
    );
  }

  return (
    <div role='list' className={cn(gridClass)} aria-label='Image grid'>
      {children}
    </div>
  );
}

GridContent.displayName = 'GridContent';

export function Grid({ json, width, height, children }: GridProps) {
  const MAX_IMGS_LG = 9;
  const MAX_IMGS_MD = 6;

  const hasChildren = !!children;
  const hasJson = json && json.length > 0;

  const childrenArray = React.Children.toArray(children);

  // Count images for grid layout logic
  // Count children if provided, otherwise use json length
  let imageCount = 0;
  if (hasChildren) {
    imageCount = childrenArray.length;
  } else if (hasJson) {
    imageCount = json.length;
  }

  let gridClass = 'mx-auto not-prose';

  if (imageCount < MAX_IMGS_MD) {
    gridClass = cn(gridClass, 'flex flex-wrap justify-center gap-lg');
  } else {
    gridClass = cn(
      gridClass,
      'grid grid-rows-3 grid-flow-col gap-2xl xl:px-4 lg:px-24 md:px-32 px-2',
    );
  }

  return (
    <div>
      {/* TODO: Enhance to avoid multiple grid loads */}
      <div className='hidden xl:block'>
        <GridContent json={json} gridClass={gridClass} width={width}>
          {children}
        </GridContent>
      </div>
      <div className='hidden xl:hidden lg:block'>
        <GridContent
          json={json?.slice(0, MAX_IMGS_LG)}
          gridClass={gridClass}
          width={width}
        >
          {childrenArray.slice(0, MAX_IMGS_LG)}
        </GridContent>
      </div>
      <div className='hidden lg:hidden md:block'>
        <GridContent
          json={json?.slice(0, MAX_IMGS_MD)}
          gridClass={gridClass}
          width={width}
        >
          {childrenArray.slice(0, MAX_IMGS_MD)}
        </GridContent>
      </div>
      <div className='block md:hidden'>
        <Slider json={json?.slice(0, 6)} height={height}>
          {childrenArray.slice(0, 6)}
        </Slider>
      </div>
    </div>
  );
}

Grid.displayName = 'Grid';
